import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import DoughnutGauge from './DoughnutGauge';
import BarChart from '../../../common/components/charts/BarChart';
import DashboardCardLeft from './DashboardCardLeftTwoCol';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

import {
  useGetAssessmentPeriodMetricBenchmarkResultsQuery,
  useGetAssessmentPeriodMetricResultsQuery,
} from 'areas/assessment-schedule/api';
import { useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { isNulogicBenchmarksEnabled } from 'common/selectors/auth';
interface DashboardPillarScoresLeftProps {
  assessment_period_id: number;
  prior_assessment_period_id?: number | null;
}

const DashboardPillarScoresLeft = ({
  assessment_period_id,
  prior_assessment_period_id,
}: DashboardPillarScoresLeftProps) => {
  const [pillarScores, setPillarScores] = useState<{ label: string; value: number }[]>([]);
  const [benchmarkScores, setBenchmarkScores] = useState<{ label: string; value: number }[]>([]);
  const [showBenchmark, setShowBenchmark] = useState(false);
  const [overallScore, setOverallScore] = useState(0);
  const [previousScore, setPreviousScore] = useState<number | null>(null);
  const navigate = useNavigate();
  const isBenchmarkFeatureEnabled = useSelector(isNulogicBenchmarksEnabled);

  const { data } = useGetAssessmentPeriodMetricResultsQuery(
    { assessmentPeriodId: assessment_period_id },
    { skip: !assessment_period_id }
  );

  const {
    data: previousData,
    isLoading: isPreviousLoading,
    isFetching: isPreviousFetching,
  } = useGetAssessmentPeriodMetricResultsQuery(
    { assessmentPeriodId: prior_assessment_period_id ?? 0 },
    {
      skip: !prior_assessment_period_id,
    }
  );

  const { data: benchmarkData } = useGetAssessmentPeriodMetricBenchmarkResultsQuery(
    { assessmentPeriodId: assessment_period_id },
    { skip: !assessment_period_id || !isBenchmarkFeatureEnabled }
  );

  useEffect(() => {
    if (data && !Array.isArray(data)) {
      const factorScoreData = data.pillars || [];
      const scores = factorScoreData.map((item: { label: string; average_response: number | string | null | undefined }) => {
        const numericValue = Number(item?.average_response ?? 0);
        return {
        label: item.label,
          value: Number.isFinite(numericValue) ? parseFloat(numericValue.toFixed(1)) : 0,
        };
      });
      setPillarScores(scores);
      const numericOverallScore = Number(data.overall_score ?? 0);
      setOverallScore(Number.isFinite(numericOverallScore) ? numericOverallScore : 0);
    } else {
      setPillarScores([]);
      setOverallScore(0);
    }
  }, [data]);

  useEffect(() => {
    if (previousData && !Array.isArray(previousData)) {
      const numericPreviousScore = Number(previousData.overall_score ?? 0);
      setPreviousScore(Number.isFinite(numericPreviousScore) ? numericPreviousScore : null);
    } else {
      setPreviousScore(null);
    }
  }, [previousData]);

  useEffect(() => {
    if (isBenchmarkFeatureEnabled && benchmarkData && !Array.isArray(benchmarkData)) {
      const benchmarkPillars = benchmarkData.pillars || [];
      const scores = benchmarkPillars
        .map((item: { label: string; mean: number | string | null | undefined }) => {
          const numericValue = Number(item?.mean ?? NaN);
          return {
            label: item.label,
            value: Number.isFinite(numericValue) ? parseFloat(numericValue.toFixed(1)) : null,
          };
        })
        .filter((item): item is { label: string; value: number } => item.value !== null);

      setBenchmarkScores(scores);
    } else {
      setBenchmarkScores([]);
    }
  }, [benchmarkData, isBenchmarkFeatureEnabled]);

  const hasPreviousPeriod = previousScore !== null;
  const hasValidPreviousPeriodId = typeof prior_assessment_period_id === 'number' && prior_assessment_period_id > 0;
  const previousDataLoaded = !isPreviousLoading && !isPreviousFetching;
  const showTrendBadge = hasValidPreviousPeriodId && previousDataLoaded && hasPreviousPeriod;
  const displayedDiff = showTrendBadge
    ? Number(overallScore.toFixed(1)) - Number(previousScore!.toFixed(1))
    : null;
  const trendVariant = displayedDiff === null
    ? 'neutral'
    : displayedDiff > 0
      ? 'positive'
      : displayedDiff < 0
        ? 'negative'
        : 'neutral';

  const hasBenchmark = isBenchmarkFeatureEnabled && benchmarkScores.length > 0;

  return (
    <DashboardCardLeft
      title="Culture Score"
      onClick={() => navigate('/app/analytics/?tab=0')}
      leftContent={
        <CardContent>
          <GaugeWrapper>
            <DoughnutGauge max={10} complete={overallScore} precision={1} size={'medium'} />
            {showTrendBadge && displayedDiff !== null && (
              <TrendBadge variant={trendVariant}>
                {displayedDiff > 0 ? (
                  <TrendingUp size={14} style={{ marginRight: '2px' }} />
                ) : displayedDiff < 0 ? (
                  <TrendingDown size={14} style={{ marginRight: '2px' }} />
                ) : (
                  <Minus size={14} style={{ marginRight: '2px' }} />
                )}
                {displayedDiff > 0 ? `+${displayedDiff.toFixed(1)}` : displayedDiff.toFixed(1)}
              </TrendBadge>
            )}
          </GaugeWrapper>
        </CardContent>
      }
      rightContent={
        <RightContentWrapper>
          <BarChart
            data={pillarScores}
            benchmarkData={isBenchmarkFeatureEnabled && showBenchmark ? benchmarkScores : []}
            multiple={10}
            percent={false}
          />
          {hasBenchmark && (
            <BenchmarkLegendRow
              onClick={event => {
                event.stopPropagation();
              }}
            >
              <BenchmarkCheckboxLabel>
                <BenchmarkCheckbox
                  type="checkbox"
                  checked={showBenchmark}
                  onChange={event => {
                    event.stopPropagation();
                    setShowBenchmark(event.target.checked);
                  }}
                  aria-label="nuLogic Benchmark"
                />
                <span>nuLogic Benchmark</span>
              </BenchmarkCheckboxLabel>
            </BenchmarkLegendRow>
          )}
        </RightContentWrapper>
      }
    />
  );
};

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 140px;
  min-width: 120px;
  max-width: 140px;
  min-height: 120px;
  gap: 12px;
`;

const GaugeWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const TrendBadge = styled.div<{ variant: 'positive' | 'negative' | 'neutral' }>`
  position: absolute;
  right: -12px;
  bottom: -8px;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 9999px;
  font-family: 'Poppins', sans-serif;
  font-size: 1rem;
  font-weight: 500;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  background-color: ${props =>
    props.variant === 'positive'
      ? 'rgba(236, 253, 245, 1)'
      : props.variant === 'negative'
      ? 'rgba(254, 242, 242, 1)'
      : 'rgba(243, 244, 246, 1)'};
  color: ${props =>
    props.variant === 'positive'
      ? '#047857'
      : props.variant === 'negative'
      ? '#b91c1c'
      : '#374151'};
`;

const RightContentWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
`;

const BenchmarkLegendRow = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 2px;
`;

const BenchmarkCheckboxLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #2e2e2e;
  font-size: 10px;
  font-weight: 400;
  white-space: nowrap;
  cursor: pointer;
`;

const BenchmarkCheckbox = styled.input`
  cursor: pointer;
`;

export default DashboardPillarScoresLeft;

